import { ICommandBase } from '@/commands/interface/iCommandBase';
import { Document } from '@/model/document2/document';
import { Design } from '@/model/document2/page/surface/design/design';
import { GroupLayer } from '@/model/document2/page/surface/design/layer/groupLayer';
import { Layer } from '@/model/document2/page/surface/design/layer/layer';
import { PlainTextLayer } from '@/model/document2/page/surface/design/layer/plainTextLayer';
import { DebugHelper } from "@/helper/debugHelper";
import { Const } from '@/const'
import { SimpleTextLayer } from '@/model/document2/page/surface/design/layer/simpleTextLayer';

// テンプレート差し替えコマンド
export class ReplaceTemplateCommand implements ICommandBase
{
    private design! : Design;
    private templateDocument! : Document;
    private isKeepRatio! : boolean;
    private rotate! : number;
    private doneCallback! : () => void;

    private oldGroupLayer! : GroupLayer;
    private oldRotate! : number;

    constructor(design : Design, templateDocument : Document, isKeepRatio : boolean, rotate : number, doneCallback : () => void)
    {
        this.design = design;
        this.templateDocument = templateDocument;
        this.isKeepRatio = isKeepRatio;
        this.rotate = rotate;
        this.doneCallback = doneCallback;

        this.oldGroupLayer = design.layer.clone();
        this.oldRotate = design.rotate;
    }

    public do() : void
    {
        // 貼り付け対象のデザインを回転
        this.design.rotateDesign(this.rotate);
        // テンプレート差し替え
        this.replaceTemplate();

        this.doneCallback();
    }

    public undo() : void
    {
        // グループレイヤークリア
        this.design.layer = GroupLayer.create();
        // 回転角
        var delta = this.oldRotate - this.design.rotate; 
        // 回転
        this.design.rotateDesign(delta);
        // グループレイヤーを元に戻す
        this.design.layer = this.oldGroupLayer;

        this.doneCallback();
    }

    public redo() : void
    {
        // 貼り付け対象のデザインを回転
        this.design.rotateDesign(this.rotate);
        // テンプレート差し替え
        this.replaceTemplate();

        this.doneCallback();
    }

    // テンプレート差し替えメイン処理
    private replaceTemplate()
    {
        if (this.templateDocument.pageCount == 0) { return; }
        // ドキュメントの1ページ目
        var page = this.templateDocument.pageList[0];
        // ページの表面
        var surface = page.getSurface(false);
        // 表面の最初のposition
        var templateDesign = surface.getDesign(0);
        if(templateDesign == null) { return; }

        // 貼り付け時の倍率
        var scaleX = this.isKeepRatio ? Math.min(this.design.width / templateDesign.width, this.design.height / templateDesign.height) : this.design.width / templateDesign.width;
        var scaleY = this.isKeepRatio ? scaleX : this.design.height / templateDesign.height;

        // デザインのレイヤーを差し替える
        this.design.layer = GroupLayer.create();

        this.pasteGroupLayer(this.design.layer, templateDesign.layer, scaleX, scaleY);
    }

    private pasteGroupLayer(target : GroupLayer, groupLayer : GroupLayer, scaleX : number, scaleY : number)
    {
        for(var i = 0; i < groupLayer.layers.length; ++i)
        {
            var layer = groupLayer.layers[i];
            if(layer instanceof GroupLayer)
            {
                var group = GroupLayer.create();
                // パラメータのコピー
                group.isExpanded = layer.isExpanded;
                group.rotate = layer.rotate;
                group.insertionId = layer.insertionId;
                group.name = layer.name;
                group.isLocked = layer.isLocked;
                this.pasteGroupLayer(group, layer, scaleX, scaleY);
                target.addLayer(group);
            }
            else
            {
                var cloned = layer.clone();
                cloned.width = layer.width * scaleX;
                cloned.height = layer.height * scaleY;
                cloned.rotate = layer.rotate;
                cloned.x = layer.x * scaleX;
                cloned.y = layer.y * scaleY;

                this.applyProfile(cloned);

                target.addLayer(cloned);        
            }
        }
    }

    // プロフィール対応
    private applyProfile(layer : Layer)
    {
        if(layer instanceof PlainTextLayer || layer instanceof SimpleTextLayer)
        {
            var arrayName = layer.name.split(Const.PROFILE_KEY_SEPARATOR);
            if(arrayName.length >= 2 && arrayName[0] == Const.PROFILE_KEY_PREFIX)
            {
                var key = arrayName[1];
                var val : string | null = null;
                if(arrayName.length >= 3)
                {
                    val = arrayName[2];
                    if(val.length == 0) { val = null; }
                }

                if(key == Const.PROFILE_KEY_NAME) { layer.profileKey = Const.PROFILE_KEY_NAME; }
                else if(key == Const.PROFILE_KEY_FURIGANA) { layer.profileKey = Const.PROFILE_KEY_FURIGANA; }
                else if(key == Const.PROFILE_KEY_NAME_ROMAN) { layer.profileKey = Const.PROFILE_KEY_NAME_ROMAN; }
                else if(key == Const.PROFILE_KEY_CELL_TEL) { layer.profileKey = Const.PROFILE_KEY_CELL_TEL; }
                else if(key == Const.PROFILE_KEY_MAIL) { layer.profileKey = Const.PROFILE_KEY_MAIL; }
                else if(key == Const.PROFILE_KEY_COMPANY) { layer.profileKey = Const.PROFILE_KEY_COMPANY; }
                else if(key == Const.PROFILE_KEY_DIV) { layer.profileKey = Const.PROFILE_KEY_DIV; }
                else if(key == Const.PROFILE_KEY_TEL) { layer.profileKey = Const.PROFILE_KEY_TEL; }
                else if(key == Const.PROFILE_KEY_FAX) { layer.profileKey = Const.PROFILE_KEY_FAX; }
                else if(key == Const.PROFILE_KEY_ZIP) { layer.profileKey = Const.PROFILE_KEY_ZIP; }
                else if(key == Const.PROFILE_KEY_ADDRESS1) { layer.profileKey = Const.PROFILE_KEY_ADDRESS1; }
                else if(key == Const.PROFILE_KEY_ADDRESS2) { layer.profileKey = Const.PROFILE_KEY_ADDRESS2; }
                else if(key == Const.PROFILE_KEY_URL) { layer.profileKey = Const.PROFILE_KEY_URL; }

                DebugHelper.trace(layer.profileKey);

                layer.name = val == null ? key : val;
            }
            else
            {
                // ふりがな対応
                if(layer.name == "ヨミ" || layer.name == "テキスト ヨミ") layer.profileKey = Const.PROFILE_KEY_FURIGANA;
                else if(layer.name == "YOMI") layer.profileKey = Const.PROFILE_KEY_NAME_ROMAN;

                DebugHelper.trace(layer.profileKey);
            }
        }
    }
}